import React, { useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MapPin, Search, X, Loader2 } from 'lucide-react';
import { useGoogleAddressSearch, fetchPlaceDetails } from '../../../utils/googleGeocoder';

/**
 * Address search field powered by Google Places (New) via our backend proxy.
 *
 * Props:
 *  - value: current text in the input
 *  - onChange: (text) => void
 *  - onSelect: (normalizedFeature) => void
 *  - placeholder
 *  - lat / lon: optional bias for ranking suggestions
 */
export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = 'Venue name or address on Google Maps…',
  testId = 'venue-address-search',
  lat,
  lon,
  label = 'Search your venue',
  helperText = 'e.g. “Nammos Mykonos” or “Ermou 15 Athens”',
}) {
  const [focused, setFocused] = useState(false);
  const [resolvingId, setResolvingId] = useState(null);
  const blurTimerRef = useRef(null);
  const { suggestions, loading, getSessionToken, rotateSession } = useGoogleAddressSearch(value, {
    lat,
    lon,
  });

  const showPanel = focused && (value || '').trim().length >= 2;

  const handlePick = async (candidate) => {
    if (!candidate?.id) return;
    setResolvingId(candidate.id);
    try {
      const feature = await fetchPlaceDetails(candidate.id, {
        sessionToken: getSessionToken(),
      });
      if (feature) {
        onSelect?.(feature);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('Place details lookup failed:', err);
    } finally {
      // End the billing session and start a fresh one for the next search.
      rotateSession();
      setResolvingId(null);
      setFocused(false);
    }
  };

  return (
    <div style={{ width: '100%', position: 'relative', marginBottom: 14 }}>
      <label
        style={{
          display: 'block',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12.5,
          fontWeight: 700,
          color: 'var(--accent-purple)',
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          marginBottom: 6,
          marginLeft: 4,
        }}
      >
        {label}
      </label>

      <div
        style={{
          background: 'var(--surface)',
          borderRadius: 16,
          padding: '14px 14px 14px 16px',
          border: `1.5px solid ${focused ? 'var(--accent-purple)' : 'var(--card-border)'}`,
          boxShadow: focused
            ? '0 0 0 4px var(--accent-surface-soft), 0 6px 18px var(--inner-shadow)'
            : 'var(--premium-shadow-nested)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          transition: 'border-color 200ms ease, box-shadow 200ms ease',
        }}
      >
        <Search size={18} color="var(--text-2)" strokeWidth={2.2} />
        <input
          type="text"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          data-testid={testId}
          onFocus={() => {
            if (blurTimerRef.current) {
              clearTimeout(blurTimerRef.current);
              blurTimerRef.current = null;
            }
            setFocused(true);
          }}
          onBlur={() => {
            if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
            blurTimerRef.current = setTimeout(() => setFocused(false), 150);
          }}
          autoComplete="street-address"
          autoCorrect="off"
          autoCapitalize="words"
          spellCheck={false}
          inputMode="text"
          enterKeyHint="search"
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            background: 'transparent',
            fontFamily: "'Inria Serif', serif",
            fontSize: 16,
            fontWeight: 500,
            color: 'var(--text)',
          }}
        />
        {loading ? (
          <Loader2 size={16} color="var(--accent-purple)" className="address-loader-spin" />
        ) : value ? (
          <button
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onChange('')}
            data-testid={`${testId}-clear`}
            aria-label="Clear search"
            style={{
              border: 'none',
              background: 'var(--accent-surface-soft)',
              borderRadius: '50%',
              width: 26,
              height: 26,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <X size={13} color="#5B4AA3" strokeWidth={2.4} />
          </button>
        ) : null}
      </div>

      <p
        data-testid={`${testId}-helper`}
        style={{
          margin: '6px 4px 0',
          fontFamily: "'Inria Serif', serif",
          fontSize: 12,
          color: 'var(--text-3)',
          lineHeight: 1.4,
        }}
      >
        {helperText}
      </p>

      <AnimatePresence>
        {showPanel ? (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            data-testid={`${testId}-panel`}
            style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              left: 0,
              right: 0,
              zIndex: 30,
              background: 'var(--surface)',
              border: '1px solid var(--card-border)',
              borderRadius: 18,
              boxShadow: '0 14px 32px rgba(20, 16, 50, 0.14)',
              overflow: 'hidden',
              maxHeight: 340,
              overflowY: 'auto',
            }}
          >
            {suggestions.length === 0 && !loading ? (
              <p
                data-testid={`${testId}-empty`}
                style={{
                  margin: 0,
                  padding: '14px 16px',
                  fontFamily: "'Inria Serif', serif",
                  fontSize: 14,
                  color: 'var(--text-3)',
                }}
              >
                No matches yet. Try the venue name or a nearby street address…
              </p>
            ) : null}

            {suggestions.map((s) => (
              <button
                type="button"
                key={s.id}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => handlePick(s)}
                data-testid={`${testId}-suggestion-${s.id}`}
                disabled={resolvingId !== null}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 12,
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'background 160ms ease',
                }}
                onMouseEnter={(event) => { event.currentTarget.style.background = 'var(--accent-surface-soft)'; }}
                onMouseLeave={(event) => { event.currentTarget.style.background = 'transparent'; }}
              >
                <span
                  style={{
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    borderRadius: 10,
                    background: 'var(--accent-surface-soft)',
                    color: 'var(--accent-purple)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MapPin size={15} strokeWidth={2.2} style={{ display: resolvingId === s.id ? 'none' : 'block' }} />
                  <Loader2 size={15} strokeWidth={2.2} className="address-loader-spin" style={{ display: resolvingId === s.id ? 'block' : 'none' }} />
                </span>
                <span style={{ minWidth: 0, flex: 1 }}>
                  <span
                    style={{
                      display: 'block',
                      fontFamily: "'Inria Serif', serif",
                      fontSize: 15,
                      fontWeight: 700,
                      color: 'var(--text)',
                      lineHeight: 1.2,
                    }}
                  >
                    {s.title}
                  </span>
                  {s.subtitle ? (
                    <span
                      style={{
                        display: 'block',
                        marginTop: 2,
                        fontFamily: "'Inria Serif', serif",
                        fontSize: 12.5,
                        color: 'var(--text-3)',
                      }}
                    >
                      {s.subtitle}
                    </span>
                  ) : null}
                </span>
              </button>
            ))}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <style>{`
        @keyframes address-loader-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .address-loader-spin { animation: address-loader-spin 0.9s linear infinite; }
      `}</style>
    </div>
  );
}
